import React, { useCallback, useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Spinner from 'react-bootstrap/Spinner'
import { useDispatch } from 'react-redux'
import ApplyReport from '../admin/curation/ApplyReport'
import MergeGroupCard from '../admin/curation/MergeGroupCard'
import { combineBest, survivorDraft } from '../admin/curation/survivorDraft'

// Merging courses you picked yourself, with the review the curation page gets.
//
// The country table used to post straight to `admin/groups/merge`, which takes a list of
// ids and nothing else. That endpoint decides the surviving course's name, description,
// directions, coordinates and metadata silently — it keeps the survivor's own if a human
// authored it and otherwise re-derives them from the layouts it just absorbed — and the
// fuller description is very often on the course being absorbed, not the one that happens
// to survive. So every merge from this page quietly threw away the best data in the group.
//
// `admin/curation/merges/preview` runs the same rules over exactly the ids selected, with
// no clustering and, deliberately, no score gate: the pairs this page exists to merge are
// precisely the ones the proposer refused. What comes back is an ordinary merge group, so
// the curation page's own card and survivor editor render it unchanged, and it is applied
// through the ordinary `merges/apply`, which validates against live rows.
//
// One selected course is a legitimate case, not a degenerate one: it comes back with no
// members, and it is the only thing that reports whether that course is hidden.

const previewMerge = (ids) => ({
  type: 'ADMIN_PREVIEW_MERGE',
  payload: {
    request: {
      url: `/admin/curation/merges/preview`,
      method: 'post',
      data: { ids },
    },
  },
})

const applyMerge = (dryRun, group) => ({
  type: 'ADMIN_APPLY_MERGE',
  payload: {
    request: {
      url: `/admin/curation/merges/apply`,
      method: 'post',
      data: { dryRun, groups: [group] },
    },
  },
})

const parse = (result) => {
  try {
    return JSON.parse(result.payload.data)
  } catch (e) {
    return null
  }
}

export default function MergePanel({ groups, onDone, onState }) {
  const dispatch = useDispatch()
  const [preview, setPreview] = useState(null)
  const [draft, setDraft] = useState(null)
  const [report, setReport] = useState(null)
  const [previewed, setPreviewed] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  // The ids, as a stable string: `groups` is rebuilt on every render of the table, so
  // depending on the array itself would refetch the preview forever.
  const key = groups.map((group) => group.id).join(',')

  useEffect(() => {
    const ids = key
      .split(',')
      .filter(Boolean)
      .map(Number)
    if (ids.length === 0) return

    let current = true
    setPreview(null)
    setDraft(null)
    setReport(null)
    setPreviewed(false)
    setError(null)
    dispatch(previewMerge(ids))
      .then((result) => {
        if (!current) return
        const group = parse(result)
        setPreview(group)
        // The survivor's flags are the only place the page can learn whether a course is
        // hidden: the public course listing carries no such field.
        if (group && onState) {
          onState({ active: group.survivor.active })
        }
      })
      .catch(() => {
        if (current) setError('Could not read these courses.')
      })
    return () => {
      current = false
    }
  }, [dispatch, key, onState])

  const bodyFor = useCallback(() => {
    if (!preview) return null
    return {
      groupKey: preview.groupKey,
      survivorId: preview.survivor.courseId,
      members: preview.members.map((member) => ({
        courseId: member.courseId,
        name: member.name,
        active: member.active,
      })),
      ...(draft ? { survivor: draft } : {}),
    }
  }, [preview, draft])

  const submit = (dryRun) => {
    const body = bodyFor()
    if (!body || busy) return

    setBusy(true)
    dispatch(applyMerge(dryRun, body))
      .then((result) => {
        setReport(parse(result))
        if (dryRun) {
          setPreviewed(true)
          return
        }
        // The rows just merged are still on screen and re-applying them would come back
        // as a wall of rejections, so the caller reloads the table and clears the
        // selection rather than leaving this panel pointing at a course that is gone.
        if (onDone) onDone()
      })
      .catch(() => setError('The merge was refused.'))
      .then(() => setBusy(false))
  }

  if (groups.length === 0) return null

  if (error) {
    return (
      <Alert variant="danger" className="mt-2 mb-0 py-2 small">
        {error}
      </Alert>
    )
  }

  if (!preview) {
    return (
      <p className="text-muted small mt-2 mb-0">
        <Spinner size="sm" className="me-2" />
        Reading {groups.length === 1 ? 'this course' : `these ${groups.length} courses`}…
      </p>
    )
  }

  // One course has nothing to merge. The preview was still worth fetching — it is what
  // told the panel above whether this course is hidden — but there is no group to show.
  if (preview.members.length === 0) return null

  return (
    <div className="mt-2">
      <ApplyReport report={report} onDismiss={() => setReport(null)} />
      <MergeGroupCard
        group={preview}
        accepted
        batched={false}
        // The editor always needs a draft to render against; `draft` staying null is what
        // says the reviewer never touched it, and an untouched group deliberately sends no
        // survivor block at all — the merge then behaves exactly as it always has.
        draft={draft || survivorDraft(preview)}
        onDraftChange={setDraft}
        onCombine={() => setDraft(combineBest(preview))}
        onReset={() => setDraft(null)}
        onPreview={() => submit(true)}
        onMerge={() => submit(false)}
        previewed={previewed}
        applying={busy}
      />
    </div>
  )
}
